/**
 * Query-key helpers. Shape: ["job", <jobId>, <endpoint>, ...params] for
 * anything scoped to a job (so an SSE change for that job invalidates all of
 * it), ["addressbook", ...] for identity, ["frontend", ...] for notes/views,
 * ["global", ...] for the rest. Slices add keys under their own endpoint
 * names using the same prefixes.
 */
import { hooksJobId, liquidityJobId, tokenJobId, type SeriesName } from "./types";

export const qk = {
  all: ["job"] as const,
  job: (jobId: string) => ["job", jobId] as const,
  jobs: () => ["global", "jobs"] as const,

  // series slice (overview_tape)
  summary: (chain: string, token: string) => ["job", tokenJobId(chain, token), "summary"] as const,
  candles: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "candles", params] as const,
  series: (chain: string, token: string, name: SeriesName, params: unknown) =>
    ["job", tokenJobId(chain, token), "series", name, params] as const,
  actions: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "actions", params] as const,
  markers: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "markers", params] as const,
  tx: (chain: string, token: string, tx: string) => ["job", tokenJobId(chain, token), "tx", tx] as const,
  positions: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "positions", params] as const,
  position: (chain: string, token: string, address: string) =>
    ["job", tokenJobId(chain, token), "position", address] as const,

  // families slice
  family: (chain: string, token: string, address: string) =>
    ["job", tokenJobId(chain, token), "family", address] as const,
  familyTimeline: (chain: string, token: string, address: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "family", address, "timeline", params] as const,
  familyRanks: (chain: string, token: string, address: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "family", address, "ranks", params] as const,
  familyGraph: (chain: string, token: string, address: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "family", address, "graph", params] as const,
  familyRecipients: (chain: string, token: string, address: string) =>
    ["job", tokenJobId(chain, token), "family", address, "recipients"] as const,
  identity: (chain: string, address: string, token?: string, scope?: string) =>
    ["addressbook", "identity", chain, address, token ?? "", scope ?? ""] as const,
  search: (params: unknown) => ["global", "search", params] as const,
  /** `GET /api/positions`: every token, so no job prefix (`keysForSseJob` maps to it) */
  myPositions: () => ["global", "my-positions"] as const,
  /** `GET /api/positions/history`: under `myPositions`, so whatever refreshes the positions refreshes it */
  myPositionsHistory: (params: unknown) => ["global", "my-positions", "history", params] as const,
  notes: (params: unknown) => ["frontend", "notes", params] as const,
  views: (params: unknown) => ["frontend", "views", params] as const,

  // holders_pnl slice
  holdersTop: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "holders", "top", params] as const,
  holdersAtLeast: (chain: string, token: string, amount: number) =>
    ["job", tokenJobId(chain, token), "holders", "at_least", amount] as const,
  holdersSamples: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "holders", "samples", params] as const,
  concentration: (chain: string, token: string) =>
    ["job", tokenJobId(chain, token), "holders", "concentration"] as const,
  cohorts: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "holders", "cohorts", params] as const,
  pnl: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "pnl", params] as const,
  supply: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "supply", params] as const,
  supplySchedule: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "supply", "schedule", params] as const,
  entry: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "entry", params] as const,
  flow: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "flow", params] as const,
  sellersByEntry: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "sellers_by_entry", params] as const,

  // liquidity_hooks slice
  liquidityEvents: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "events", params] as const,
  liquidityRanges: (chain: string, token: string, params?: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "ranges", params ?? {}] as const,
  liquidityBook: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "book", params] as const,
  liquidityPools: (chain: string, token: string) =>
    ["job", tokenJobId(chain, token), "liquidity", "pools"] as const,
  liquidityDepth: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "depth", params] as const,
  liquidityDepthSpots: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "depth", "spots", params] as const,
  liquidityDepthTrend: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "depth", "trend", params] as const,
  liquidityProfile: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "profile", params] as const,
  liquidityHeatmap: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "heatmap", params] as const,
  liquidityPoolFees: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "pool-fees", params] as const,
  liquidityFlow: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "flow", params] as const,
  liquidityManagers: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "managers", params] as const,
  liquidityWalls: (chain: string, token: string, params: unknown) =>
    ["job", tokenJobId(chain, token), "liquidity", "walls", params] as const,
  liquidityLayout: (chain: string, token: string) =>
    ["job", tokenJobId(chain, token), "liquidity", "layout"] as const,
  ledgers: (chain: string, params: unknown) => ["job", liquidityJobId(chain), "ledgers", params] as const,
  ledger: (chain: string, contract: string, params: unknown) =>
    ["job", liquidityJobId(chain), "ledger", contract, params] as const,
  ledgerSeries: (chain: string, contract: string, params: unknown) =>
    ["job", liquidityJobId(chain), "ledger", contract, "series", params] as const,
  liquiditySignals: (chain: string, params: unknown) =>
    ["job", liquidityJobId(chain), "signals", params] as const,
  hookLeaderboard: (chain: string, params: unknown) =>
    ["job", hooksJobId(chain), "leaderboard", params] as const,
  hookHours: (chain: string, params: unknown) => ["job", hooksJobId(chain), "hours", params] as const,
  hook: (chain: string, hook: string) => ["job", hooksJobId(chain), "hook", hook] as const,
  hookHourSeries: (chain: string, hook: string, params: unknown) =>
    ["job", hooksJobId(chain), "hook", hook, "hours", params] as const,
  hookFamilies: (chain: string) => ["job", hooksJobId(chain), "families"] as const,

  // alerts slice (under "frontend": the engine writes to frontend.sqlite,
  // so its `change` event invalidates rules, the log and the summary)
  rules: (params: unknown) => ["frontend", "rules", params] as const,
  alertLog: (params: unknown) => ["frontend", "alerts", params] as const,
  alertsSummary: () => ["frontend", "alerts", "summary"] as const,
  pushKey: () => ["frontend", "push", "key"] as const,
  pushSubscriptions: () => ["frontend", "push", "subscriptions"] as const,

  // signals_health slice
  signals: (params: unknown) => ["global", "signals", params] as const,
  study: (params: unknown) => ["global", "signals", "study", params] as const,
  health: () => ["global", "health"] as const,
  jobHealth: (jobId: string) => ["global", "health", jobId] as const,
  logs: (name: string, lines: number | undefined) => ["global", "health", "logs", name, lines] as const,
} as const;

/**
 * What an SSE `change` for `job` should invalidate.
 *
 * Data changes are *frequent* — the hooks store commits every ~2 s on the
 * live stores — so the keys must be as narrow as the change really is.
 * `["global"]` is the prefix of health (which polls on its own 15 s timer)
 * and search (which no store row can invalidate), and a whole-prefix
 * invalidation of it refetches both every two seconds for nothing.
 */
export function keysForSseJob(job: string): readonly (readonly string[])[] {
  // positions read every token job and the owned marks
  if (job === "addressbook")
    return [["addressbook"], ["job"], ["global", "search"], ["global", "my-positions"]];
  if (job === "frontend") return [["frontend"], ["global", "signals"], ["global", "search"]];
  // new rows for a job move that job's read models, the job list's head
  // blocks / counts, the signals the job can raise and, for a token job,
  // the owned positions in it — nothing else.
  return [
    ["job", job],
    ["global", "jobs"],
    ["global", "signals"],
    ...(job.startsWith("token:") ? [["global", "my-positions"]] : []),
  ];
}
